/**
 * The pe3d variant list's tree: the base's folders (versionHierarchy nodes)
 * with its variants placed in them, and the rules for dragging one onto
 * another (components/pe3d/containerPe3dVariantList.js).
 */

export const ROOT = 0;

/** Folders and variants as nested nodes; variants the tree does not know go to the root. */
export function buildVariantTree(tree, variants, searchTerm = "") {
  const term = searchTerm.trim().toLowerCase();
  const byId = new Map(variants.map((item) => [String(item._id), item]));
  const placed = new Set();
  const folders = (tree || []).filter((node) => node.droppable === true);
  const folderIds = new Set(folders.map((node) => String(node.id)));
  const childrenOf = (parentId) => {
    const out = [];
    for (const folder of folders.filter((node) => String(node.parent) === String(parentId))) {
      out.push({ type: "folder", id: folder.id, title: folder.text, children: childrenOf(folder.id) });
    }
    for (const node of (tree || []).filter((n) => n.droppable !== true && String(n.parent) === String(parentId))) {
      const variant = byId.get(String(node.id));
      if (!variant) continue;
      placed.add(String(node.id));
      out.push({ type: "variant", id: String(node.id), title: variant.title, variant });
    }
    return out;
  };
  const roots = childrenOf(ROOT);
  // Variants whose node is missing, or sits under a folder that no longer exists.
  for (const variant of variants) {
    const node = (tree || []).find((n) => n.droppable !== true && String(n.id) === String(variant._id));
    if (!placed.has(String(variant._id)) && (!node || !folderIds.has(String(node.parent)))) {
      roots.push({ type: "variant", id: String(variant._id), title: variant.title, variant });
    }
  }
  if (!term) return roots;
  const matchesFolder = (node) => String(node.title || "").toLowerCase().includes(term);
  const matchesVariant = (node) => `${node.title} ${node.variant.game}`.toLowerCase().includes(term);

  // A match keeps the folders ABOVE it, so its place in the base is still
  // readable — and nothing else. A folder that neither matches nor has a
  // surviving child is dropped, which is what takes the match's siblings off
  // the list while the way down to it stays.
  //
  // A folder that matches by its own name is kept WHOLE: asking for the folder
  // is asking for what is in it, not for the one variant inside that happens
  // to repeat the folder's name.
  const filter = (nodes) =>
    nodes
      .map((node) => {
        if (node.type !== "folder") return node;
        return matchesFolder(node) ? node : { ...node, children: filter(node.children) };
      })
      .filter((node) => (node.type === "folder"
        ? matchesFolder(node) || node.children.length > 0
        : matchesVariant(node)));
  return filter(roots);
}

/** The folder node with this id, anywhere in the tree; null for ROOT or a miss. */
export function findFolder(nodes, id) {
  if (id === undefined || id === null || String(id) === String(ROOT)) return null;
  for (const node of nodes) {
    if (node.type !== "folder") continue;
    if (String(node.id) === String(id)) return node;
    const found = findFolder(node.children, id);
    if (found) return found;
  }
  return null;
}

/**
 * The folders leading to this one, outermost first: [{ id, title }].
 *
 * Empty at the top level, which the path bar shows as its own first crumb
 * rather than as an entry here — there is no folder to name.
 */
export function folderTrail(nodes, id, trail = []) {
  if (id === undefined || id === null || String(id) === String(ROOT)) return [];
  for (const node of nodes) {
    if (node.type !== "folder") continue;
    const here = [...trail, { id: node.id, title: node.title }];
    if (String(node.id) === String(id)) return here;
    const deeper = folderTrail(node.children, id, here);
    if (deeper.length) return deeper;
  }
  return [];
}

/** Every variant id under a folder, depth first. */
export function variantIdsUnder(node) {
  if (node.type === "variant") return [node.id];
  return node.children.flatMap(variantIdsUnder);
}

/**
 * The tree as a flat list of folders that hold variants directly, top level
 * first, for a checklist: [{ key, path, variants }]. `path` is the folder names
 * joined ("Autumn / Hard"), empty for the top level.
 */
export function variantGroups(nodes, path = [], key = String(ROOT), out = []) {
  const own = nodes.filter((node) => node.type === "variant").map((node) => node.variant);
  if (own.length) out.push({ key, path: path.join(" / "), variants: own });
  for (const node of nodes) {
    if (node.type === "folder") variantGroups(node.children, [...path, node.title], String(node.id), out);
  }
  return out;
}

/** Each node's parent folder (ROOT at the top), and each folder's own subfolders, from the unfiltered tree. */
export function treeIndex(nodes, parent = ROOT, index = { parentOf: {}, foldersUnder: {} }) {
  for (const node of nodes) {
    index.parentOf[`${node.type}-${node.id}`] = parent;
    if (node.type === "folder") {
      treeIndex(node.children, node.id, index);
      index.foldersUnder[node.id] = node.children
        .filter((child) => child.type === "folder")
        .flatMap((child) => [String(child.id), ...index.foldersUnder[child.id]]);
    }
  }
  return index;
}

/** Whether `dragged` may be dropped into folder `target` (ROOT for the top level). */
export function canDropInto(dragged, target, index) {
  if (!dragged) return false;
  const key = `${dragged.type}-${dragged.id}`;
  if (String(index.parentOf[key] === undefined ? ROOT : index.parentOf[key]) === String(target)) return false;
  if (dragged.type !== "folder") return true;
  return String(dragged.id) !== String(target) && !(index.foldersUnder[dragged.id] || []).includes(String(target));
}
